import { useState, useRef, useEffect } from 'react';
import { RotateCcw, RotateCw, Volume2, VolumeX, Play, Pause, Maximize } from 'lucide-react';

const VideoPlayer = ({ src, playbackUrl, poster, thumbnail, captionsUrl }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const controlsTimeout = useRef(null);

  // Set initial volume when video loads
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.volume = 1;
      video.muted = false;
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      // Ensure volume is set when metadata loads
      video.volume = volume;
      video.muted = false;
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);
    const handleCanPlay = () => {
      setIsBuffering(false);
      // Ensure audio is enabled when video can play
      video.muted = false;
      video.volume = volume;
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const rewind = () => {
    const video = videoRef.current;
    video.currentTime = Math.max(0, video.currentTime - 10);
  };

  const forward = () => {
    const video = videoRef.current;
    video.currentTime = Math.min(video.duration, video.currentTime + 10);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * video.duration;
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const toggleCaptions = () => {
    const video = videoRef.current;
    if (video.textTracks && video.textTracks.length > 0) {
      const track = video.textTracks[0];
      if (captionsEnabled) {
        track.mode = 'hidden';
        setCaptionsEnabled(false);
      } else {
        track.mode = 'showing';
        setCaptionsEnabled(true);
      }
    } else {
      setCaptionsEnabled(!captionsEnabled);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  return (
    <div 
      className="relative bg-black rounded-xl overflow-hidden group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      style={{ minHeight: '200px', maxHeight: '400px' }}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        poster={poster || thumbnail}
        playsInline
        preload="auto"
        muted={false}
        onClick={togglePlay}
        style={{ minHeight: '200px', maxHeight: '400px' }}
      >
        <source src={playbackUrl || src} type="video/mp4" />
        <source src={src} type="video/webm" />
        {captionsUrl && (
          <track 
            kind="captions" 
            src={captionsUrl} 
            srcLang="en" 
            label="English" 
            default={captionsEnabled}
          />
        )}
        Your browser does not support the video tag.
      </video>

      {/* Buffering indicator */}
      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
        </div>
      )}

      {/* Play button overlay when paused */}
      {!isPlaying && !isBuffering && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlay}
        >
          <div className="bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all">
            <Play className="w-12 h-12 text-white fill-white" />
          </div>
        </div>
      )}

      {/* Custom Controls */}
      <div 
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Progress bar */}
        <div 
          className="w-full h-2 bg-gray-600 rounded-full cursor-pointer mb-3 group/progress"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-coral rounded-full relative"
            style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover/progress:opacity-100 transition-opacity"></div>
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Rewind 10s */}
            <button 
              onClick={rewind}
              className="text-white hover:text-coral transition-colors p-1"
              title="Rewind 10 seconds"
            >
              <RotateCcw className="w-5 h-5" />
            </button>

            {/* Play/Pause */}
            <button 
              onClick={togglePlay}
              className="text-white hover:text-coral transition-colors p-1"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 fill-white" />
              ) : (
                <Play className="w-6 h-6 fill-white" />
              )}
            </button>

            {/* Forward 10s */}
            <button 
              onClick={forward}
              className="text-white hover:text-coral transition-colors p-1"
              title="Forward 10 seconds"
            >
              <RotateCw className="w-5 h-5" />
            </button>

            {/* Volume controls */}
            <div className="flex items-center space-x-2 group/volume">
              <button 
                onClick={toggleMute}
                className="text-white hover:text-coral transition-colors p-1"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-coral"
              />
            </div>

            {/* Time display */}
            <span className="text-white text-sm font-medium ml-2">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Right side controls */}
          <div className="flex items-center space-x-2">
            {/* Captions toggle - CC button */}
            <button 
              onClick={toggleCaptions}
              className={`transition-colors px-2 py-1 rounded text-sm font-bold border-2 ${
                captionsEnabled 
                  ? 'bg-coral text-white border-coral' 
                  : 'text-white border-white hover:bg-white hover:text-black'
              }`}
              title={captionsEnabled ? 'Disable captions' : 'Enable captions'}
            >
              CC
            </button>

            {/* Fullscreen */}
            <button 
              onClick={toggleFullscreen}
              className="text-white hover:text-coral transition-colors p-1"
              title="Fullscreen"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
